// The Call-out Hub (Session AQW, Phase 2, W-6): a call-out's own record page, keyed by the CallOut
// id, at /office/callouts/:id. Before this session a call-out had no page of its own -- "this table
// has no per-row detail page" (the audit's own citation). This is that page: the pipeline status,
// the SLA clock (honest per the CALL-OUT SLA CLOCK RULE -- never a fabricated pass), the originating
// call, the assigned engineer, the attendance sheet with approval inline (the shared
// ServiceRecordPanel, now richer for every caller), the charge, the invoice, photos, the attendance
// report, a follow-up tech visit action, and the audit timeline -- reusing the EXISTING modals
// (CalloutChargeModal, CalloutPhotosModal, CalloutReportModal, CalloutReportRefModal) rather than
// rebuilding their logic, exactly as the office Call-outs list already does.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { CalloutDetail, JobSheetListItem, AuditTimelineEntry, AssignableEngineer, ScrubSummary, CalloutListItem } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { formatSlaMinutes, formatSlaTarget } from "../../lib/slaFormat";
import { formatDate, formatDateTime, gbpExact } from "../../lib/format";
import { RecordHeader } from "../../components/RecordHeader";
import { AccountSymbol } from "../../components/AccountSymbol";
import { RecordLink, RecordLinkGroup } from "../../components/RecordLink";
import { StatusPill } from "../../components/StatusPill";
import { primaryStatePill } from "../../lib/statusRegistry";
import { Button, buttonClasses } from "../../components/Button";
import { ProposedSlotLine } from "../../components/ProposedSlotLine";
import { Loader } from "../../components/Loader";
import { statusEntry } from "../../lib/statusRegistry";
import { ConfirmButton } from "../../components/ConfirmButton";
import { ConfirmDeleteModal, DELETE_CONFIRM_MESSAGE } from "../../components/ConfirmDeleteModal";
import { EmptyState } from "../../components/EmptyState";
import { AuditTimeline } from "../../components/AuditTimeline";
import { CalloutChargeModal } from "./CalloutChargeModal";
import { CalloutPhotosModal } from "./CalloutPhotosModal";
import { CalloutReportModal } from "./CalloutReportModal";
import { buildReportInitial, storedFormSiteId } from "./calloutReportSeed";
import { CalloutReportRefModal } from "./CalloutReportRefModal";
import { ServiceRecordPanel, MoreActionsMenu, type MoreActionItem } from "./ServiceRecordPanel";
import { RepairEstimatesPanel } from "./RepairEstimatesPanel";
import { SlaHoldPanel } from "./SlaHoldPanel";
import { LiftContractDoor } from "./LiftContractDoor";

function gbpPence(p: number | null): string {
  return p == null ? "Not recorded" : gbpExact(p / 100);
}

function SlaLegRow({ label, outcome, targetMins, mins }: { label: string; outcome: string | null; targetMins: number | null; mins: number | null }) {
  const o = outcome ?? "unmeasured";
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 py-1">
      <span className="text-ele-body">{label}</span>
      {/* flex-wrap on both (Session BDY): the target text and the outcome pill ran 13px past a 360px phone at a 1.3x text size. */}
      <span className="flex flex-wrap items-center justify-end gap-2 text-right">
        <span className="text-xs text-ele-body opacity-70">
          Target {formatSlaTarget(targetMins)}
          {mins != null ? ` · ${formatSlaMinutes(mins)}` : ""}
        </span>
        <StatusPill domain="slaOutcome" status={o} />
      </span>
    </div>
  );
}

// The subset of fields CalloutChargeModal/CalloutPhotosModal/CalloutReportRefModal need, adapted
// from this page's CalloutDetail (a superset of CalloutListItem after Session AQW's widening).
function toListItemShape(d: CalloutDetail): CalloutListItem {
  return {
    id: d.id,
    ref: d.ref,
    reference: d.reference,
    siteName: d.siteName,
    customerName: d.customerName,
    siteId: d.siteId,
    customerId: d.customerId,
    assetId: d.assetId,
    assetRef: d.assetRef,
    faultDescription: d.faultDescription,
    reportedAt: d.reportedAt,
    attendedAt: d.attendedAt,
    status: d.status,
    isEmergency: d.isEmergency,
    isNonContract: d.isNonContract,
    isEntrapment: d.isEntrapment,
    slaDeadline: d.slaDeadline,
    completedAt: d.completedAt,
    engineerName: d.engineerName,
    jobSheetJobId: d.jobSheetJobId,
    reportRef: d.reportRef,
    hasReportPdf: d.hasReportPdf,
    reportSentAt: d.reportSentAt,
    reportSentTo: d.reportSentTo,
  };
}

type BannerState = { kind: "ok" | "error"; message: string } | null;

export function CalloutHubPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Session ASM (the D57 rollout gap): assigning an engineer to a call-out is admin AND office, matching
  // PATCH /api/callouts/:id/assign, GET /api/callouts/engineers and every other surface D57 widened (the Call-outs
  // list's row actions already use canDispatch). Delete stays admin only (isAdmin above).
  const canDispatch = user?.role === "admin" || user?.role === "office";

  const [detail, setDetail] = useState<CalloutDetail | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error" | "not_found">("loading");
  const [banner, setBanner] = useState<BannerState>(null);
  const [busy, setBusy] = useState(false);

  // The attendance sheet (the call-out's own dispatch job), loaded separately once the call-out
  // detail names a jobSheetJobId. A call-out awaiting its first engineer, or attended but not yet
  // submitted, has none -- shown as such rather than a blank panel.
  const [sheet, setSheet] = useState<JobSheetListItem | null>(null);
  const [sheetState, setSheetState] = useState<"idle" | "loading" | "ready" | "error">("idle");

  const [entries, setEntries] = useState<AuditTimelineEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(true);
  const [auditError, setAuditError] = useState<string | null>(null);

  // Resources for the report modal's own pickers + the assign/reassign control (admin and office for the latter,
  // D57, matching PATCH /api/callouts/:id/assign's own guard).
  // Session SC2B: the site of a stored report form's lift, when that lift is not the call-out's own (see calloutReportSeed.ts).
  const [storedSiteId, setStoredSiteId] = useState<string | null>(null);
  const [engineers, setEngineers] = useState<AssignableEngineer[]>([]);

  const [chargeOpen, setChargeOpen] = useState(false);
  const [photosOpen, setPhotosOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [refOpen, setRefOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [assignEngineerId, setAssignEngineerId] = useState("");
  // Session BCS: the engineer picker opens from a small "Change engineer" action beside the Engineer field.
  const [changingEngineer, setChangingEngineer] = useState(false);
  // Session BCS: set the moment Release for sending succeeds, so Complete unlocks without reloading
  // the page (a reload would unmount the panel and lose its approval notes).
  const [approvedHere, setApprovedHere] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const [techOpen, setTechOpen] = useState(false);
  const [techDescription, setTechDescription] = useState("");
  const [techEngineerId, setTechEngineerId] = useState("");
  const [techRaisedBy, setTechRaisedBy] = useState("");
  const [techTwoMan, setTechTwoMan] = useState(false);
  const [techBusy, setTechBusy] = useState(false);
  const [techError, setTechError] = useState<string | null>(null);

  // Per-call-out UI state never carries to another call-out (the component is reused across :id).
  useEffect(() => {
    setApprovedHere(false);
    setChangingEngineer(false);
  }, [id]);

  const load = useCallback(async () => {
    if (!id) return;
    setState("loading");
    try {
      const d = await api.callouts.get(id);
      setDetail(d);
      setState("ready");
    } catch (err) {
      setState(err instanceof ApiError && err.status === 404 ? "not_found" : "error");
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!id) return;
    let live = true;
    setAuditLoading(true);
    setAuditError(null);
    api.auditLog
      .list("callout", id)
      .then((res) => {
        if (live) setEntries(res.entries);
      })
      .catch((err) => {
        if (live) setAuditError(friendlyApiError(err, "Could not load the activity for this record."));
      })
      .finally(() => {
        if (live) setAuditLoading(false);
      });
    return () => {
      live = false;
    };
  }, [id]);

  const loadSheet = useCallback((jobId: string) => {
    setSheetState("loading");
    api.jobsheets
      .get(jobId)
      .then((s) => {
        setSheet(s);
        setSheetState("ready");
      })
      .catch(() => setSheetState("error"));
  }, []);

  useEffect(() => {
    if (detail?.jobSheetJobId) loadSheet(detail.jobSheetJobId);
    else {
      setSheet(null);
      setSheetState("idle");
    }
  }, [detail?.jobSheetJobId, loadSheet]);

  // The report modal reads the lift and site it needs from the call-out's own record and searches the rest, so this page loads no register
  // (Session SC2B). Only a stored form for a DIFFERENT lift needs one read of that lift, for the site picker.
  useEffect(() => {
    let live = true;
    setStoredSiteId(null);
    if (detail) {
      void storedFormSiteId(detail).then((id) => {
        if (live) setStoredSiteId(id);
      });
    }
    return () => {
      live = false;
    };
  }, [detail]);

  useEffect(() => {
    if (!canDispatch) return;
    api.callouts
      .engineers()
      .then(setEngineers)
      .catch(() => undefined);
  }, [canDispatch]);

  function ok(message: string) {
    setBanner({ kind: "ok", message });
  }
  function fail(message: string) {
    setBanner({ kind: "error", message });
  }

  const cancellable = detail != null && detail.status !== "complete" && detail.status !== "cancelled";
  // Session BCS: Complete waits for an engineer's sheet to be approved (the server refuses it too).
  const approvalNeeded = detail != null && detail.reportApprovalRequired && !approvedHere;

  function goToApproveStep() {
    document.getElementById("attendance-record")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // The detail is re-read quietly after an approval (no loading state, so the panel stays mounted).
  async function refreshDetailQuietly() {
    if (!id) return;
    try {
      setDetail(await api.callouts.get(id));
    } catch {
      /* approvedHere already unlocked Complete; the next full load corrects the rest */
    }
  }

  // Session SC5: Accept on the proposed engineer passes that engineer; the picker passes the chosen one. One route either way.
  async function assign(engineerId: string = assignEngineerId) {
    if (!detail || !engineerId) return;
    setAssigning(true);
    try {
      await api.callouts.assign(detail.id, engineerId);
      setAssignEngineerId("");
      setChangingEngineer(false);
      await load();
      ok("Engineer assigned.");
    } catch (err) {
      fail(friendlyApiError(err, "Could not assign an engineer. Please try again."));
    } finally {
      setAssigning(false);
    }
  }

  async function completeCallout() {
    if (!detail) return;
    setBusy(true);
    try {
      await api.callouts.complete(detail.id);
      await load();
      ok("Marked complete.");
    } catch (err) {
      fail(friendlyApiError(err, "Could not complete this call-out. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function cancelCallout() {
    if (!detail) return;
    setBusy(true);
    try {
      await api.callouts.cancel(detail.id);
      await load();
      ok("Cancelled.");
    } catch (err) {
      fail(friendlyApiError(err, "Could not cancel this call-out. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function raiseInvoice() {
    if (!detail) return;
    setBusy(true);
    try {
      const res = await api.callouts.createInvoice(detail.id);
      await load();
      ok(res.invoiced ? "Xero draft invoice raised." : "Could not raise a Xero draft (Xero may not be connected).");
    } catch (err) {
      fail(friendlyApiError(err, "Could not raise the invoice. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!detail) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const summary: ScrubSummary = await api.callouts.delete(detail.id);
      navigate("/office/callouts", {
        state: { deletedRef: detail.reference ?? detail.ref, xeroVoided: summary.xeroVoided, stripeDeleted: summary.stripeDeleted },
      });
    } catch (err) {
      setDeleteError(friendlyApiError(err, "Could not delete this call-out. Please try again."));
      setDeleting(false);
    }
  }

  async function raiseTechVisit() {
    if (!detail?.assetId || !techDescription.trim()) return;
    setTechBusy(true);
    setTechError(null);
    try {
      const res = await api.schedule.techVisit({
        assetId: detail.assetId,
        description: techDescription.trim(),
        originCalloutId: detail.id,
        ...(techEngineerId ? { assignedTo: techEngineerId } : {}),
        ...(techRaisedBy.trim() ? { raisedBy: techRaisedBy.trim() } : {}),
        ...(techTwoMan ? { twoManBooked: true } : {}),
      });
      setTechOpen(false);
      setTechDescription("");
      setTechEngineerId("");
      setTechRaisedBy("");
      setTechTwoMan(false);
      await load();
      ok(
        res.reused
          ? "This lift already had an open tech visit, so nothing new was raised. See the follow-up visits below."
          : "Tech visit raised onto the Scheduling backlog, unassigned until the office plans it in.",
      );
    } catch (err) {
      setTechError(friendlyApiError(err, "Could not raise the tech visit. Please try again."));
    } finally {
      setTechBusy(false);
    }
  }

  const reportInitial = useMemo(() => (detail ? buildReportInitial(detail, storedSiteId) : null), [detail, storedSiteId]);

  if (state === "loading") {
    return (
      <div className="p-4 sm:p-6">
        <Loader shape="panel" />
      </div>
    );
  }
  if (state === "not_found") {
    return (
      <div className="p-4 sm:p-6">
        <EmptyState
          message="That call-out could not be found."
          action={
            <Link to="/office/callouts" className="text-sm text-ele-blue underline">
              Back to Call-outs
            </Link>
          }
        />
      </div>
    );
  }
  if (state === "error" || !detail) {
    return (
      <div className="p-4 sm:p-6">
        <EmptyState
          message="Could not load this call-out."
          action={
            <button type="button" onClick={() => void load()} className="text-sm text-ele-blue underline">
              Retry
            </button>
          }
        />
      </div>
    );
  }

  const clockUnmeasured = detail.slaStartAt == null;

  // Session BCS: Cancel and Delete live in one More menu. Cancel arms on its first click and fires on
  // the second (the menu's confirm item); Delete opens the branded two-step modal.
  const headerMoreItems: MoreActionItem[] = [];
  if (cancellable) headerMoreItems.push({ key: "cancel", label: "Cancel call-out", tone: "danger", confirm: true, onSelect: () => void cancelCallout() });
  if (isAdmin) headerMoreItems.push({ key: "delete", label: "Delete call-out", tone: "danger", onSelect: () => setDeleteOpen(true) });

  // SC9A fix round 1: the call-out's ONE state pill (entrapment, on hold with its reason, SLA held), from the registry; null shows the status word.
  const hubState = primaryStatePill({
    entrapment: detail.isEntrapment,
    status: detail.holdReason ? "parts_pending" : undefined,
    holdReason: detail.holdReason,
    holdReasonText: detail.holdReasonText,
    slaHeldLabel: detail.slaOnHold ? detail.slaHeldLabel : null,
  });

  return (
    <div className="p-4 sm:p-6">
      <RecordHeader
        title={detail.reference ?? detail.ref}
        backTo="/office/callouts"
        backLabel="Back to Call-outs"
        status={
          hubState ? <StatusPill state={hubState} /> : <StatusPill domain="job" status={detail.status} />
        }
        subtitle={
          <>
            {detail.customerId ? (
              <Link to={`/office/customers/${detail.customerId}`} className="text-ele-purple hover:underline">
                {detail.customerName}
              </Link>
            ) : (
              detail.customerName
            )}
            {detail.customerId && <AccountSymbol customerId={detail.customerId} />}
            {" · "}
            {detail.siteId ? (
              <Link to={`/office/sites/${detail.siteId}`} className="text-ele-purple hover:underline">
                {detail.siteName}
              </Link>
            ) : (
              detail.siteName
            )}
            {/* An entrapment is ONE red pill, in the header (SC9A fix round 1); an emergency that is not an entrapment keeps its type tag here. */}
            {detail.isEmergency && !detail.isEntrapment && (
              <StatusPill word="Emergency" tone="red" variant="solid" className="ml-2" />
            )}
            {detail.assetId && (
              <>
                {" · "}
                <Link to={`/office/lifts/${detail.assetId}`} className="text-ele-purple hover:underline">
                  {detail.assetRef ?? "Lift"}
                </Link>
              </>
            )}
            {detail.isNonContract && <StatusPill word="Non-contract" tone="amber" className="ml-2" />}
          </>
        }
        actions={
          <>
            {cancellable && (
              <ConfirmButton
                onConfirm={completeCallout}
                disabled={busy || approvalNeeded}
                busy={busy}
                label="Complete"
                busyLabel="Completing..."
                confirmLabel={detail.isEmergency || detail.isEntrapment ? "Confirm? Sends survey" : "Confirm complete?"}
                className={buttonClasses("secondary", "md")}
              />
            )}
            {cancellable && approvalNeeded && (
              <button type="button" onClick={goToApproveStep} className="text-sm text-ele-blue underline">
                Release the report first
              </button>
            )}
            <MoreActionsMenu ariaLabel="More actions for this call-out" items={headerMoreItems} />
          </>
        }
      />

      {banner && (
        <p
          role={banner.kind === "error" ? "alert" : "status"}
          className={`mt-4 rounded-ele border px-3 py-2 text-sm ${
            banner.kind === "error" ? "border-ele-danger text-ele-danger" : "border-ele-green text-ele-green"
          }`}
        >
          {banner.message}
        </p>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <section className="rounded-ele border border-ele-hairline bg-ele-surface p-4">
            <h2 className="font-heading text-lg tracking-wide text-ele-strong">Summary</h2>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-ele-body opacity-70">Raised</dt>
                <dd className="text-ele-strong">{formatDateTime(detail.reportedAt)}</dd>
              </div>
              <div>
                <dt className="text-ele-body opacity-70">Engineer assigned</dt>
                <dd className="text-ele-strong">{formatDateTime(detail.assignedAt)}</dd>
              </div>
              <div>
                <dt className="text-ele-body opacity-70">Attended</dt>
                <dd className="text-ele-strong">{formatDateTime(detail.attendedAt)}</dd>
              </div>
              <div>
                <dt className="text-ele-body opacity-70">Marked complete</dt>
                <dd className="text-ele-strong">{formatDateTime(detail.completedAt)}</dd>
              </div>
              <div>
                <dt className="text-ele-body opacity-70">Engineer</dt>
                <dd className="text-ele-strong">
                  {detail.engineerName ?? "Unassigned"}
                  {canDispatch && cancellable && (
                    <button type="button" onClick={() => setChangingEngineer((o) => !o)} className="ml-2 text-xs text-ele-blue underline">
                      {changingEngineer ? "Close" : detail.engineerName ? "Change engineer" : "Assign engineer"}
                    </button>
                  )}
                </dd>
              </div>
              {detail.callLogId && (
                <div>
                  <dt className="text-ele-body opacity-70">Origin</dt>
                  <dd className="text-ele-strong">Raised from a logged call</dd>
                </div>
              )}
            </dl>
            {detail.faultDescription && (
              <div className="mt-3">
                <p className="font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70">Fault reported</p>
                <p className="mt-0.5 text-sm text-ele-strong">{detail.faultDescription}</p>
              </div>
            )}
            {detail.notes && (
              <div className="mt-3">
                <p className="font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70">Notes</p>
                <p className="mt-0.5 whitespace-pre-wrap text-sm text-ele-body">{detail.notes}</p>
              </div>
            )}

            {/* Session SC5: an unassigned call-out shows the platform's proposed engineer with one Accept, with no "Assign engineer" press first. The picker is today's flow. */}
            {canDispatch && cancellable && !detail.engineerName && !changingEngineer && (
              <div className="mt-4">
                <ProposedSlotLine target={{ calloutId: detail.id }} busy={assigning} onAccept={(p) => (p.engineerId ? assign(p.engineerId) : undefined)} />
              </div>
            )}
            {canDispatch && cancellable && changingEngineer && (
              <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-ele-hairline pt-3">
                <label className="text-sm">
                  <span className="mb-1 block text-xs text-ele-body opacity-70">{detail.engineerName ? "Reassign to" : "Assign to"}</span>
                  <select
                    value={assignEngineerId}
                    onChange={(e) => setAssignEngineerId(e.target.value)}
                    className="rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green"
                  >
                    <option value="">Choose an engineer</option>
                    {engineers.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.name}
                      </option>
                    ))}
                  </select>
                </label>
                <Button variant="secondary" size="sm" onClick={() => void assign()} disabled={!assignEngineerId || assigning}>
                  {assigning ? "Assigning..." : detail.engineerName ? "Reassign" : "Assign"}
                </Button>
              </div>
            )}
          </section>

          {/* Session BBJ: the renewal funnel doors (a lift with no live contract, or a contracted site's renewal). */}
          <LiftContractDoor
            hasLift={detail.assetId != null}
            liftContract={detail.liftContract}
            customerId={detail.customerId}
            siteId={detail.siteId}
            showRenewal
          />

          <section className="rounded-ele border border-ele-hairline bg-ele-surface p-4">
            <h2 className="font-heading text-lg tracking-wide text-ele-strong">SLA clock</h2>
            {/* Session ATC (D18): two SEQUENTIAL legs. Response is logged to an engineer assigned; fix is the
                engineer's arrival to sign-off. The travel between them is not clocked. */}
            {clockUnmeasured && (
              <p className="mt-2 text-sm text-ele-body opacity-70">
                Unmeasured: no time of day was recorded when this call-out was raised, so the response clock cannot be judged honestly.
              </p>
            )}
            <div className="mt-2 divide-y divide-ele-hairline">
              <SlaLegRow label="Response (logged to engineer assigned)" outcome={detail.slaResponseOutcome} targetMins={detail.slaResponseTargetMins} mins={detail.slaResponseMins} />
              <SlaLegRow label="Fix (arrival to sign-off)" outcome={detail.slaFixOutcome} targetMins={detail.slaFixTargetMins} mins={detail.slaFixMins} />
            </div>
            {detail.slaHeldMins != null && detail.slaHeldMins > 0 && (
              <p className="mt-2 text-xs text-ele-body opacity-70">
                The response time leaves out {formatSlaMinutes(detail.slaHeldMins)} on SLA hold, waiting on the customer.
              </p>
            )}
            {detail.slaZoneCode && <p className="mt-2 text-xs text-ele-body opacity-70">Zone: {detail.slaZoneCode}</p>}
            {/* Session AUT: the SLA hold (one clock, plus holds). Placed, released, corrected and removed here. */}
            <SlaHoldPanel calloutId={detail.id} hasClock={!clockUnmeasured && detail.status !== "cancelled"} onChanged={() => void load()} />
          </section>

          <section className="rounded-ele border border-ele-hairline bg-ele-surface p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-heading text-lg tracking-wide text-ele-strong">Charge</h2>
              <Button variant="ghost" size="sm" onClick={() => setChargeOpen(true)}>
                {detail.totalChargePence != null ? "Edit charge" : "Compute charge"}
              </Button>
            </div>
            {detail.totalChargePence != null ? (
              <>
                <dl className="mt-2 grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <dt className="text-ele-body opacity-70">Labour</dt>
                    <dd className="text-ele-strong">{gbpPence(detail.labourChargePence)}</dd>
                  </div>
                  <div>
                    <dt className="text-ele-body opacity-70">Travel</dt>
                    <dd className="text-ele-strong">{gbpPence(detail.travelChargePence)}</dd>
                  </div>
                  <div>
                    <dt className="text-ele-body opacity-70">Total (ex VAT)</dt>
                    <dd className="text-ele-strong">{gbpPence(detail.totalChargePence)}</dd>
                  </div>
                </dl>
                {detail.breakdown && <p className="mt-2 text-xs text-ele-body opacity-70">{detail.breakdown}</p>}
              </>
            ) : (
              <p className="mt-2 text-sm text-ele-body opacity-70">No charge computed yet.</p>
            )}
            {detail.agreedPricePence != null && (
              <p className="mt-2 text-sm text-ele-strong">
                Agreed price: {gbpPence(detail.agreedPricePence)}
                {detail.agreedPriceReason ? `. ${detail.agreedPriceReason}` : ""}
              </p>
            )}
            {/* Session ASW (D22): what the always-on caller check found when this emergency was logged, and the
                office's choice. Shown only on an emergency logged since the check existed. */}
            {detail.callerMatch && (
              <div className="mt-3 text-sm">
                <p className="text-ele-body opacity-70">Existing customer check</p>
                {detail.callerMatch.failed ? (
                  <p className="text-ele-body">The check could not run when this call-out was logged.</p>
                ) : detail.callerMatch.candidates.length === 0 ? (
                  <p className="text-ele-body">No existing customer matched the caller.</p>
                ) : (
                  <p className="text-ele-body">
                    Possible match: {detail.callerMatch.candidates.map((c) => c.customerName).join(", ")}.
                  </p>
                )}
                <p className="text-ele-strong">
                  {detail.agreedPricePence != null
                    ? "Billed at the agreed price above."
                    : detail.contractRateApplied
                      ? `Billed at ${detail.contractRateApplied.customerName ?? "the matched customer"}'s contract rate (the in-contract Core rate), by the office's choice.`
                      : "Billed at the non-contract rate."}
                </p>
              </div>
            )}
          </section>

          <section className="rounded-ele border border-ele-hairline bg-ele-surface p-4">
            <h2 id="attendance-record" className="font-heading text-lg tracking-wide text-ele-strong">Attendance record</h2>
            {!detail.jobId ? (
              <p className="mt-2 text-sm text-ele-body opacity-70">Not yet assigned to an engineer, so there is no visit to record.</p>
            ) : !detail.jobSheetJobId ? (
              <p className="mt-2 text-sm text-ele-body opacity-70">Assigned, but the engineer has not submitted a job sheet yet.</p>
            ) : sheetState === "loading" || sheetState === "idle" ? (
              <Loader shape="inline" className="mt-2" />
            ) : sheetState === "error" || !sheet ? (
              <p className="mt-2 text-sm text-ele-danger">Could not load the attendance record.</p>
            ) : (
              <div className="mt-3">
                <ServiceRecordPanel
                  sheet={sheet}
                  omitFaultReported={
                    !sheet.faultReported?.trim() || sheet.faultReported.trim().toLowerCase() === (detail.faultDescription ?? "").trim().toLowerCase()
                  }
                  onSent={(jobId, sentAt, emailSentTo) =>
                    setSheet((s) => (s && s.jobId === jobId ? { ...s, sentToClientAt: sentAt, ...(emailSentTo !== undefined ? { emailSentTo } : {}) } : s))
                  }
                  onApproved={(jobId, res) => {
                    setApprovedHere(true);
                    void refreshDetailQuietly();
                    setSheet((s) =>
                      s && s.jobId === jobId
                        ? { ...s, jobStatus: "complete", emailSentAt: new Date().toISOString(), emailSentTo: res.emailSentTo, driveFileUrl: res.driveFileUrl }
                        : s,
                    );
                  }}
                  onChanged={() => detail.jobSheetJobId && loadSheet(detail.jobSheetJobId)}
                  onAmended={() => detail.jobSheetJobId && loadSheet(detail.jobSheetJobId)}
                />
              </div>
            )}
          </section>

          <RecordLinkGroup
            title="Follow-up tech visits"
            count={detail.originatedTechVisits.length}
            empty="None raised from this call-out."
            action={
              detail.assetId ? (
                <Button variant="ghost" size="sm" onClick={() => setTechOpen((o) => !o)}>
                  {techOpen ? "Cancel" : "Raise a follow-up tech visit"}
                </Button>
              ) : undefined
            }
          >
            {detail.originatedTechVisits.map((v) => (
              <RecordLink
                key={v.jobId}
                to={`/office/jobsheets/${v.jobId}`}
                label={v.description ?? "Tech visit"}
                meta={`${statusEntry("job", v.status).label}${v.engineerName ? ` · ${v.engineerName}` : " · Unassigned"}${v.scheduledFor ? ` · ${formatDate(v.scheduledFor)}` : ""}`}
              />
            ))}
          </RecordLinkGroup>

          {techOpen && (
            <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
              <h3 className="font-heading text-sm uppercase tracking-wide text-ele-blue">Raise a follow-up tech visit</h3>
              <p className="mt-1 text-xs text-ele-body opacity-70">
                Charged as a flat day rate, above the repair rate, because this call-out diagnosed work beyond routine repair.
              </p>
              <label className="mt-3 block text-sm">
                <span className="mb-1 block text-ele-body">What does it need to do?</span>
                <textarea
                  rows={2}
                  value={techDescription}
                  onChange={(e) => setTechDescription(e.target.value)}
                  className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green"
                />
              </label>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {/* Session ASM (D57): the tech-visit route is admin and office, so the engineer picker is too. */}
                {canDispatch && (
                  <label className="text-sm">
                    <span className="mb-1 block text-ele-body">Engineer (optional)</span>
                    <select
                      value={techEngineerId}
                      onChange={(e) => setTechEngineerId(e.target.value)}
                      className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green"
                    >
                      <option value="">Leave unassigned</option>
                      {engineers.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label className="text-sm">
                  <span className="mb-1 block text-ele-body">Raised by (optional)</span>
                  <input
                    value={techRaisedBy}
                    onChange={(e) => setTechRaisedBy(e.target.value)}
                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green"
                  />
                </label>
              </div>
              <label className="mt-3 flex items-center gap-2 text-sm text-ele-body">
                <input type="checkbox" checked={techTwoMan} onChange={(e) => setTechTwoMan(e.target.checked)} />
                Booked as a two-man job
              </label>
              {techError && <p role="alert" className="mt-2 text-sm text-ele-danger">{techError}</p>}
              <div className="mt-3">
                <Button variant="primary" size="sm" onClick={raiseTechVisit} disabled={techBusy || !techDescription.trim()}>
                  {techBusy ? "Raising..." : "Raise tech visit"}
                </Button>
              </div>
            </section>
          )}

          {/* Session ARL (W-9): the price of the repair this call-out found, recorded before the
              customer has agreed it. Reachable with no lift on the register, like the call-out itself. */}
          <RepairEstimatesPanel calloutId={detail.id} className="rounded-ele border border-ele-hairline bg-ele-surface p-4" />
        </div>

        <div className="space-y-6">
          <section className="rounded-ele border border-ele-hairline bg-ele-surface p-4">
            <h2 className="font-heading text-lg tracking-wide text-ele-strong">Invoice</h2>
            {detail.xeroInvoiceId ? (
              <>
                <a
                  href={`https://go.xero.com/AccountsReceivable/Edit.aspx?InvoiceID=${encodeURIComponent(detail.xeroInvoiceId)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-block text-sm text-ele-blue underline"
                >
                  {detail.linkedInvoiceNumber ? `Invoice ${detail.linkedInvoiceNumber} (View in Xero)` : "View in Xero"}
                </a>
                {detail.linkedInvoiceCacheId && (
                  <Link to={`/office/invoices/${detail.linkedInvoiceCacheId}`} className="mt-1 block text-sm text-ele-blue underline">
                    Open invoice detail
                  </Link>
                )}
              </>
            ) : detail.totalChargePence != null ? (
              <>
                <p className="mt-2 text-sm text-ele-body opacity-70">No invoice raised yet.</p>
                <Button variant="ghost" size="sm" onClick={raiseInvoice} disabled={busy} className="mt-2">
                  Raise Xero draft
                </Button>
              </>
            ) : (
              <p className="mt-2 text-sm text-ele-body opacity-70">Compute the charge before raising an invoice.</p>
            )}
          </section>

          <section className="rounded-ele border border-ele-hairline bg-ele-surface p-4">
            <h2 className="font-heading text-lg tracking-wide text-ele-strong">Photos</h2>
            <p className="mt-2 text-sm text-ele-body opacity-70">{detail.photoCount} photo{detail.photoCount === 1 ? "" : "s"} on file.</p>
            <Button variant="ghost" size="sm" onClick={() => setPhotosOpen(true)} className="mt-2">
              View photos
            </Button>
          </section>

          <section className="rounded-ele border border-ele-hairline bg-ele-surface p-4">
            <h2 className="font-heading text-lg tracking-wide text-ele-strong">Attendance report</h2>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button variant="ghost" size="sm" onClick={() => setReportOpen(true)}>
                {detail.hasReport ? "Regenerate report" : "Generate report"}
              </Button>
              {detail.hasReportPdf && (
                <a
                  href={api.callouts.reportPdfHref(detail.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-ele border border-ele-blue px-3 py-1.5 text-xs text-ele-blue transition hover:bg-ele-blue hover:text-ele-bg"
                >
                  Open PDF
                </a>
              )}
              {detail.reportRef && (
                <button type="button" onClick={() => setRefOpen(true)} className="rounded-ele border border-ele-purple-accent px-3 py-1.5 text-xs text-ele-body hover:text-ele-strong">
                  Correct reference
                </button>
              )}
            </div>
            {detail.reportRef && <p className="mt-2 text-xs text-ele-body opacity-70">Reference: {detail.reportRef}</p>}
            {detail.reportSentAt ? (
              <p className="mt-1 text-xs text-ele-green">Released to {detail.reportSentTo ?? "the customer"} on {formatDate(detail.reportSentAt)}.</p>
            ) : detail.hasReportPdf ? (
              <p className="mt-1 text-xs text-amber-400">Not yet released.</p>
            ) : null}
          </section>

          <section className="rounded-ele border border-ele-hairline bg-ele-surface p-4">
            <h2 className="font-heading text-lg tracking-wide text-ele-strong">Activity</h2>
            <div className="mt-3">
              <AuditTimeline entries={entries} loading={auditLoading} error={auditError} />
            </div>
          </section>
        </div>
      </div>

      {chargeOpen && <CalloutChargeModal callout={toListItemShape(detail)} onClose={() => setChargeOpen(false)} onCharged={() => void load()} />}
      {photosOpen && <CalloutPhotosModal callout={toListItemShape(detail)} onClose={() => setPhotosOpen(false)} />}
      {refOpen && <CalloutReportRefModal callout={toListItemShape(detail)} onClose={() => setRefOpen(false)} onSaved={() => void load()} />}
      {reportOpen && (
        <CalloutReportModal
          initial={reportInitial}
          onClose={() => setReportOpen(false)}
          onGenerated={() => void load()}
          onReleased={() => void load()}
        />
      )}

      <ConfirmDeleteModal
        open={deleteOpen}
        title={`Delete call-out ${detail.reference ?? detail.ref}?`}
        message={DELETE_CONFIRM_MESSAGE}
        confirmLabel="Delete call-out"
        busy={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => {
          if (deleting) return;
          setDeleteOpen(false);
          setDeleteError(null);
        }}
      />
    </div>
  );
}
